import { Button, MAX_PITCH, clamp, type InputCommand } from '@game/shared';

export interface InputSettings {
  /** Radians of view rotation per unit of raw mouse movement. */
  sensitivity: number;
  /** Multiplier applied while aiming down sights. */
  adsSensitivityScale: number;
  invertY: boolean;
  /** Radians of view rotation per pixel of touch drag. */
  touchSensitivity: number;
}

const DEFAULT_SETTINGS: InputSettings = {
  sensitivity: 0.0022,
  adsSensitivityScale: 0.7,
  invertY: false,
  touchSensitivity: 0.006,
};

/**
 * `MouseEvent.button` numbers, as bit positions in `mouseButtons`. Spelled out
 * because 1 is the *wheel*, not the right button — binding aim to `1 << 1` is an
 * easy and invisible mistake.
 */
const MOUSE_LEFT = 1 << 0;
const MOUSE_RIGHT = 1 << 2;

const KEY_BUTTONS: Record<string, number> = {
  KeyW: Button.Forward,
  KeyS: Button.Back,
  KeyA: Button.Left,
  KeyD: Button.Right,
  Space: Button.Jump,
  ControlLeft: Button.Crouch,
  KeyC: Button.Crouch,
  ShiftLeft: Button.Sprint,
  KeyR: Button.Reload,
  KeyE: Button.Interact,
};

/**
 * Collects raw input and turns it into the tick-sized `InputCommand` that is the
 * only thing the client is allowed to send. Mouse movement is accumulated
 * between ticks rather than sampled, so a 1000 Hz mouse loses no precision at a
 * 64 Hz tick rate.
 */
export class InputManager {
  settings: InputSettings = { ...DEFAULT_SETTINGS };

  yaw = 0;
  pitch = 0;
  weapon = 0;

  /** Camera-only recoil offset, decayed toward zero. Folded into the sent angles. */
  private recoilPitch = 0;
  private recoilYaw = 0;

  private readonly held = new Set<string>();
  private mouseButtons = 0;
  private seq = 0;
  private locked = false;
  private readonly disposers: (() => void)[] = [];

  /**
   * Touch state, written by TouchControls and the on-screen buttons. Kept as
   * ready-made button bits so `sample()` just ORs them in — by the time a
   * command exists, keyboard and touch are indistinguishable.
   */
  touchMode = false;
  private touchButtons = 0;
  private touchMoveButtons = 0;
  /**
   * Aim-assist friction, written by the engine each tick: < 1 while aiming
   * down sights with an enemy near the crosshair, so the reticle "sticks"
   * instead of skating past — the standard mobile-shooter slowdown assist.
   */
  touchAimAssistScale = 1;

  constructor(private readonly canvas: HTMLCanvasElement) {}

  get pointerLocked(): boolean {
    return this.locked;
  }

  attach(): void {
    // Keystrokes aimed at a form field (the chat bar, the menu inputs) must
    // never double as game input — typing "wasd" is not a request to strafe.
    const typing = (event: KeyboardEvent): boolean => {
      const target = event.target;
      return (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      );
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || typing(event)) return;
      if (event.code === 'Escape') return;
      if (this.locked) event.preventDefault();
      this.held.add(event.code);
      const slot = /^Digit([1-4])$/.exec(event.code);
      if (slot) this.weapon = Number(slot[1]) - 1;
    };
    const onKeyUp = (event: KeyboardEvent) => this.held.delete(event.code);
    const onBlur = () => {
      this.held.clear();
      this.mouseButtons = 0;
    };

    const onMouseDown = (event: MouseEvent) => {
      if (!this.locked) return;
      this.mouseButtons |= 1 << event.button;
    };
    const onMouseUp = (event: MouseEvent) => {
      this.mouseButtons &= ~(1 << event.button);
    };
    const onMouseMove = (event: MouseEvent) => {
      if (!this.locked) return;
      const ads = (this.mouseButtons & MOUSE_RIGHT) !== 0;
      const scale =
        this.settings.sensitivity * (ads ? this.settings.adsSensitivityScale : 1);
      this.yaw -= event.movementX * scale;
      this.pitch += event.movementY * scale * (this.settings.invertY ? 1 : -1);
      this.pitch = clamp(this.pitch, -MAX_PITCH, MAX_PITCH);
    };
    const onWheel = (event: WheelEvent) => {
      if (!this.locked) return;
      event.preventDefault();
      this.weapon = (this.weapon + (event.deltaY > 0 ? 1 : 3)) % 4;
    };
    const onContextMenu = (event: MouseEvent) => {
      if (this.locked) event.preventDefault();
    };
    const onPointerLockChange = () => {
      this.locked = document.pointerLockElement === this.canvas;
      if (!this.locked) onBlur();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('pointerlockchange', onPointerLockChange);

    this.disposers.push(
      () => window.removeEventListener('keydown', onKeyDown),
      () => window.removeEventListener('keyup', onKeyUp),
      () => window.removeEventListener('blur', onBlur),
      () => window.removeEventListener('mousedown', onMouseDown),
      () => window.removeEventListener('mouseup', onMouseUp),
      () => window.removeEventListener('mousemove', onMouseMove),
      () => window.removeEventListener('wheel', onWheel),
      () => window.removeEventListener('contextmenu', onContextMenu),
      () => document.removeEventListener('pointerlockchange', onPointerLockChange),
    );
  }

  dispose(): void {
    for (const dispose of this.disposers) dispose();
    this.disposers.length = 0;
  }

  requestPointerLock(): void {
    // No pointer to lock on a touch device (iOS has no API for it at all).
    if (this.touchMode || !this.canvas.requestPointerLock) return;
    // Browsers reject this when the page has no recent user gesture, and an
    // unhandled rejection would surface as a console error every round start.
    // Failing is fine: the pause overlay stays up and offers a click to resume.
    const result: unknown = this.canvas.requestPointerLock();
    if (result instanceof Promise) result.catch(() => {});
  }

  /** An on-screen button being pressed (`held` true) or released. */
  setTouchButton(bit: number, held: boolean): void {
    if (held) this.touchButtons |= bit;
    else this.touchButtons &= ~bit;
  }

  /** The joystick's current direction, already quantised to move bits. */
  setTouchMove(buttons: number): void {
    this.touchMoveButtons = buttons;
  }

  /** A look-drag delta in pixels, applied like mouse movement. */
  applyTouchLook(dx: number, dy: number): void {
    const ads = (this.touchButtons & Button.Ads) !== 0;
    const scale =
      this.settings.touchSensitivity *
      (ads ? this.settings.adsSensitivityScale * this.touchAimAssistScale : 1);
    this.yaw -= dx * scale;
    this.pitch += dy * scale * (this.settings.invertY ? 1 : -1);
    this.pitch = clamp(this.pitch, -MAX_PITCH, MAX_PITCH);
  }

  /** Called by the game when a shot is fired locally, to kick the view. */
  addRecoil(pitchKick: number, yawKick: number): void {
    this.recoilPitch += pitchKick;
    this.recoilYaw += (Math.random() * 2 - 1) * yawKick;
  }

  /** Recoil decays back toward the player's own aim, like most modern shooters. */
  decayRecoil(dt: number): void {
    const decay = Math.exp(-8 * dt);
    this.recoilPitch *= decay;
    this.recoilYaw *= decay;
  }

  get viewYaw(): number {
    return this.yaw + this.recoilYaw;
  }

  get viewPitch(): number {
    return clamp(this.pitch + this.recoilPitch, -MAX_PITCH, MAX_PITCH);
  }

  /** Builds the command for the next tick. */
  sample(): InputCommand {
    let buttons = this.touchButtons | this.touchMoveButtons;
    for (const code of this.held) {
      const bit = KEY_BUTTONS[code];
      if (bit !== undefined) buttons |= bit;
    }
    if ((this.mouseButtons & MOUSE_LEFT) !== 0) buttons |= Button.Fire;
    if ((this.mouseButtons & MOUSE_RIGHT) !== 0) buttons |= Button.Ads;

    return {
      seq: ++this.seq,
      buttons,
      yaw: this.viewYaw,
      pitch: this.viewPitch,
      weapon: this.weapon,
    };
  }
}
